import React from 'react';
import { Container } from '@ui';
import { useIsTornadoEmployee } from '@hooks/user/useCurrentUser';
import { formatStepName } from '../utils/formatStepName';
import { getGPTRecordUrl } from '../utils/getGPTRecordUrl';
import { AgentOutput } from '../AgentOutput';
import { FormattedJson } from './FormattedJson';
import styled from 'styled-components';

const StepHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const StepName = styled.span`
  font-weight: 600;
  font-size: 11px;
  line-height: 14px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  opacity: 0.6;
`;

const GPTLink = styled.a`
  font-size: 10px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  opacity: 0.4;
`;

const CollapseIndicator = styled.span`
  font-size: 10px;
  opacity: 0.4;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 11px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  opacity: 0.4;
  color: ${({ theme }) => theme.themeColors.text};

  &:hover {
    opacity: 0.7;
  }
`;

function useStepParsedOutput(output) {
  const [parsedOutput, setParsedOutput] = React.useState(null);
  React.useEffect(() => {
    try {
      setParsedOutput(JSON.parse(output));
    } catch {
      setParsedOutput(null);
    }
  }, [output]);
  return parsedOutput;
}

const RawDataToggle = ({ data }) => {
  const [showAll, setShowAll] = React.useState(false);
  return (
    <Container>
      <Container top={8} row alignRight>
        <ToggleButton onClick={() => setShowAll(!showAll)}>
          {showAll ? '[Hide Plan JSON]' : '[Show Plan JSON]'}
        </ToggleButton>
      </Container>
      {showAll && <FormattedJson value={data} fontSize="12px" />}
    </Container>
  );
};

export const PlanStep = ({ data, defaultExpanded = true }) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const isTornadoEmployee = useIsTornadoEmployee();
  const parsedOutput = useStepParsedOutput(data?.output);

  if (!data?.output) return null;

  return (
    <Container top={16}>
      <StepHeading onClick={() => setExpanded(!expanded)}>
        <CollapseIndicator>{expanded ? '▼' : '▶'}</CollapseIndicator>
        <StepName>{formatStepName(data.step_id)}</StepName>
        {parsedOutput && isTornadoEmployee && (
          <GPTLink
            href={getGPTRecordUrl(parsedOutput.id)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            GPT-{parsedOutput.id}
          </GPTLink>
        )}
      </StepHeading>
      {expanded && (
        <Container top={8} left={16}>
          <AgentOutput output={data.output} />
          <RawDataToggle data={data} />
        </Container>
      )}
    </Container>
  );
};
